<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog'
import { Plus, Edit, Trash2 } from 'lucide-vue-next'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type { Agent } from '@/types/Agent'

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters.getMember)

const agents = ref<Agent[]>([])
const loading = ref(false)

const dialogOpen = ref(false)
const editing = ref<Agent | null>(null)
const form = ref({ name: '', description: '', emoji: '', prompt: '', skill_keys: '', sort_order: 0 })

const confirmDeleteDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDelete = ref<Agent | null>(null)

const fetchAgents = async () => {
  loading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/agents` })
    agents.value = response.data
  } catch (error) {
    toast({ title: 'Error', description: 'Failed to fetch agents', variant: 'destructive' })
  } finally {
    loading.value = false
  }
}

const openCreate = () => {
  editing.value = null
  form.value = { name: '', description: '', emoji: '', prompt: '', skill_keys: '', sort_order: 0 }
  dialogOpen.value = true
}

const openEdit = (agent: Agent) => {
  editing.value = agent
  form.value = {
    name: agent.name,
    description: agent.description || '',
    emoji: agent.emoji || '',
    prompt: agent.prompt || '',
    skill_keys: (agent.skill_keys || []).join(', '),
    sort_order: agent.sort_order ?? 0
  }
  dialogOpen.value = true
}

const saveAgent = async () => {
  if (!form.value.name.trim()) {
    toast({ title: 'Name is required', variant: 'destructive' })
    return
  }
  if (!form.value.prompt.trim()) {
    toast({ title: 'Prompt is required', variant: 'destructive' })
    return
  }
  try {
    const data = {
      name: form.value.name,
      description: form.value.description || null,
      emoji: form.value.emoji || null,
      prompt: form.value.prompt,
      skill_keys: form.value.skill_keys.split(',').map(k => k.trim()).filter(Boolean),
      sort_order: Number(form.value.sort_order) || 0
    }
    if (editing.value) {
      await API({ method: 'PATCH', url: `${HOST}/admin/agents/${editing.value.id}`, data })
      toast({ title: 'Agent updated' })
    } else {
      await API({ method: 'POST', url: `${HOST}/admin/agents`, data })
      toast({ title: 'Agent created' })
    }
    dialogOpen.value = false
    await fetchAgents()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to save agent'
    toast({ title: message, variant: 'destructive' })
  }
}

const promptDelete = (agent: Agent) => {
  pendingDelete.value = agent
  confirmDeleteDialog.value?.open()
}

const deleteAgent = async () => {
  if (!pendingDelete.value) return
  try {
    await API({ method: 'DELETE', url: `${HOST}/admin/agents/${pendingDelete.value.id}` })
    toast({ title: 'Agent deleted' })
    await fetchAgents()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to delete agent'
    toast({ title: message, variant: 'destructive' })
  } finally {
    pendingDelete.value = null
  }
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchAgents()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-4 flex-1 overflow-auto">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold">Agents</h1>
        <Button variant="default" size="sm" @click="openCreate">
          <Plus class="mr-2 h-4 w-4" />
          New Agent
        </Button>
      </div>

      <div class="text-sm text-muted-foreground">
        Personas that project members can @-mention in project chat.
        Showing {{ agents.length }} agent{{ agents.length !== 1 ? 's' : '' }}
      </div>

      <div v-if="loading" class="p-4 space-y-3">
        <div v-for="i in 5" :key="i" class="h-10 bg-muted rounded animate-pulse"></div>
      </div>

      <div v-else class="border rounded-md">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Handle</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Persona</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Skills</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="agent in agents" :key="agent.id" class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle font-medium">
                <span class="mr-2">{{ agent.emoji || '🤖' }}</span>{{ agent.name }}
              </td>
              <td class="p-4 align-middle">
                <code class="text-xs text-muted-foreground">@{{ agent.key }}</code>
              </td>
              <td class="p-4 align-middle max-w-md">
                <div class="text-sm text-muted-foreground truncate">
                  {{ agent.prompt || '—' }}
                </div>
              </td>
              <td class="p-4 align-middle text-sm text-muted-foreground">
                {{ (agent.skill_keys || []).join(', ') || '—' }}
              </td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(agent)" title="Edit agent">
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="promptDelete(agent)" title="Delete agent">
                    <Trash2 class="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </td>
            </tr>
            <tr v-if="agents.length === 0">
              <td colspan="5" class="text-center py-8 text-muted-foreground text-sm">No agents yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Dialog v-model:open="dialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ editing ? 'Edit Agent' : 'New Agent' }}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <div class="grid grid-cols-[1fr_5rem] gap-3">
            <div class="space-y-1">
              <Label>Name</Label>
              <Input v-model="form.name" placeholder="e.g. Analyst" />
            </div>
            <div class="space-y-1">
              <Label>Emoji</Label>
              <Input v-model="form.emoji" placeholder="🔬" />
            </div>
          </div>
          <div class="space-y-1">
            <Label>Description</Label>
            <Input v-model="form.description" placeholder="Short description shown in the agent library" />
          </div>
          <div class="space-y-1">
            <Label>Persona prompt</Label>
            <Textarea v-model="form.prompt" rows="6" placeholder="Who this agent is and how it should answer in chat" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1">
              <Label>Skill keys</Label>
              <Input v-model="form.skill_keys" placeholder="daily_review" />
            </div>
            <div class="space-y-1">
              <Label>Sort order</Label>
              <Input v-model="form.sort_order" type="number" />
            </div>
          </div>
          <p class="text-xs text-muted-foreground">
            Skill keys are described to the agent so it can talk about what runs on the project.
            The agent cannot trigger them.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="dialogOpen = false">Cancel</Button>
          <Button @click="saveAgent">{{ editing ? 'Save' : 'Create' }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DialogConfirm
      ref="confirmDeleteDialog"
      title="Delete agent?"
      description="This soft-deletes the agent. Projects that added it will no longer be able to mention it in chat. This action cannot be undone."
      @confirm="deleteAgent"
    />
  </div>
</template>
